import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNotification } from 'Context/NotificationContext';
import dayjs from 'dayjs';
import { v4 as uuid } from 'uuid';
import useHistoryPush from 'Hooks/useHistoryPush';
import useWindowSize from 'Hooks/useWindowSize';
import { useQueryNotifications } from 'Hooks/notifications/useQueryNotifications';
import { useQueryReadNotification } from 'Hooks/notifications/useQueryReadNotification';
import { useQueryReadAllNotifications } from 'Hooks/notifications/useQueryReadAllNotifications';
import { useQueryReadMultipleNotifications } from 'Hooks/notifications/useQueryReadMultipleNotifications';
import { getCurrentTenant } from 'Store/Tenant/Tenant.selector';
import { ActivitiesActions } from 'Store/Activities/Activities.actions';
import { getTheme } from 'Store/MultiDomain/MultiDomain.selector';
import {
  INotificationOrder,
  INotificationOrderKey,
} from 'Store/Notification/interfaces/INotificationState';
import {
  arrayNotificationKeys,
  FiltersRecordType,
  IFiltroOptions,
  IUpdateFilter,
} from 'Data/interfaces/Notification/IFilters';
import { IObraResponse } from 'Data/interfaces/Obra/IObraListResponse';
import { IUserInforResponse } from 'Data/interfaces/Auth/IUserInfoResponse';
import {
  filterItemAuxIdByActivityAction,
  filterItemIdByActivityAction,
  INewNotificationsFilters,
  INotification,
  INotificationMultipleReadRequest,
  NotificationTypeEnum,
  NotificationActionEnum,
} from 'Data/interfaces/Notification/INotification';
import { BcfImportApi } from 'Data/BcfImport.api';
import PageHeader from 'Components/UI/PageHeader/PageHeader';
import BreadCrumb, { IBreadcrumbItem } from 'Components/UI/BreadCrumb';
import Table from 'Components/UI/Table';
import TableActionTopbar, {
  ITableActionTopbarButton,
} from 'Components/UI/TableActionTopbar';
import LoadingNewNotification from 'Components/UI/CustomLoading/LoadingNewNotification';
import MoreFiltersActions, {
  IOptionsMoreFiltersActions,
} from 'Components/UI/MoreFiltersActions';
import Filters from '../Filters';
import { tableColumns } from './Notification.columns';
import AppStorage from 'Utils/AppStorage';
import { isObjectEmpty } from 'Utils/ObjectUtils';
import { NOTIFICATION_FILTERS_KEY } from './Notification.helpers';
import SpinCustom from 'Components/UI/SpinCustom';
import {
  NewNotificationGlobalStyles,
  Space,
  SwitchStyled,
  WrapperTable,
} from './styles';
import { message } from 'antd';

interface INewNotification {
  userInfo?: IUserInforResponse;
  obrasList?: IObraResponse[];
  isShellCollapsed: boolean;
  externalGlobalLoading: boolean;
  onToggle: () => void;
}

const NewNotification: FC<INewNotification> = ({
  userInfo,
  obrasList,
  isShellCollapsed,
  externalGlobalLoading,
  onToggle,
}) => {
  const { t } = useTranslation('notification');
  const theme = useSelector(getTheme);
  const currentTenant = useSelector(getCurrentTenant);
  const { height, width } = useWindowSize();
  const leftBarAndPaddingWidth = isShellCollapsed ? 132 : 307;

  const breadCrumb: IBreadcrumbItem[] = [
    {
      description: t('page.breadcrumbCenter'),
      href: '/notification',
    },
  ];

  const dispatch = useDispatch();
  const { historyPush } = useHistoryPush();
  const {
    readNotificationAsync,
    isLoading: isLoadingReadNotification,
  } = useQueryReadNotification();
  const {
    readMultipleNotificationsAsync,
    isLoading: isLoadingReadMultipleNotifications,
  } = useQueryReadMultipleNotifications();
  const {
    readAllNotificationsAsync,
    isLoading: isLoadingReadAllNotifications,
  } = useQueryReadAllNotifications();
  const { addNotificationsToRead } = useNotification();

  const firstOpenFilters = useRef(true);
  const firstRenderFilters = useRef(true);
  const blockFilterDropdown = useRef(true);
  const [currentSelectedRows, setCurrentSelectedRow] = useState<INotification[]>([]);
  const [order, setOrder] = useState<INotificationOrder>({
    notifications: null,
    users: null,
    data: null,
    obra: null,
    last: null,
  });
  const [showOnlyUnread, setShowOnlyUnread] = useState(false);
  const [filters, setFilters] = useState<FiltersRecordType>({} as FiltersRecordType);
  const [showFilters, setShowFilters] = useState(false);
  const [lastFilterUpdateId, setLastFilterUpdateId] = useState('');
  const [readingNotificationIds, setReadingNotificationIds] = useState<string[]>([]);

  const filtersQuery = useMemo(() => {
    const data: INewNotificationsFilters = {};
    if (!isObjectEmpty(filters)) {
      for (const filter of Object.values(filters)) {
        if (filter.key) {
          if (filter.key === 'creationDate') {
            let value1 = null;
            let value2 = null;
            if (Array.isArray(filter.values) && filter.values.length > 1) {
              value1 = dayjs(filter.values[0]);
              value2 = dayjs(filter.values[1]);
            } else {
              value1 = dayjs(filter.values);
            }
            switch (filter.operation) {
              case 'equal':
                data.creationDateFrom = value1.format('YYYY-MM-DD');
                data.creationDateTo = value1.format('YYYY-MM-DD');
                break;
              case 'between':
                if (value1 && value2) {
                  data.creationDateFrom = value1.format('YYYY-MM-DD');
                  data.creationDateTo = value2.format('YYYY-MM-DD');
                }
                break;
              case 'before':
                data.creationDateTo = value1.subtract(1, 'day').format('YYYY-MM-DD');
                break;
              case 'after':
                data.creationDateFrom = value1.add(1, 'day').format('YYYY-MM-DD');
                break;
              case 'onOrBefore':
                data.creationDateTo = value1.format('YYYY-MM-DD');
                break;
              case 'onOrAfter':
                data.creationDateFrom = value1.format('YYYY-MM-DD');
                break;
            }
          } else {
            data[filter.key] = filter.key === 'isRead'
              ? filter?.values?.[0]
              : filter?.values;
          }
        }
      }
    }
    return data;
  }, [filters]);

  const {
    data: dataNotifications,
    isLoading: isLoadingListAll,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useQueryNotifications(20, filtersQuery);

  const notifications = useMemo(() => {
    const validNotifications: INotification[] = [];
    for (const notification of dataNotifications) {
      if (notification?.NotificationId) {
        validNotifications.push(notification);
      }
    }
    return validNotifications;
  }, [dataNotifications]);

  const obrasMap = useMemo(() => {
    const obj: Record<number, IObraResponse> = {};
    if (obrasList) {
      for (const obra of obrasList) {
        if (
          notifications.some((x) => x.ConstructionSiteFk === obra.ConstructionSiteId) &&
          !obj.hasOwnProperty(obra.ConstructionSiteId)
        ) {
          obj[obra.ConstructionSiteId] = obra;
        }
      }
    }
    return obj;
  }, [obrasList, notifications]);

  const handleShowFilters = (show: boolean) => {
    if (show) {
      blockFilterDropdown.current = true;
    }
    setShowFilters(show);
  };

  const addFilter = () => {
    blockFilterDropdown.current = false;
    let hasUpdate = false;
    const id = uuid();
    setFilters((prev) => {
      const newAddCurrentArrayTopicKeys = arrayNotificationKeys.filter((key) => {
        return !Object.values(prev).some((filter) => filter.key === key);
      });
      const addkey = newAddCurrentArrayTopicKeys.pop();

      if (addkey) {
        hasUpdate = true;
        prev[id] = { id, key: addkey } as IFiltroOptions;
      }

      return { ...prev };
    });
    if (hasUpdate) {
      setLastFilterUpdateId(id);
    }
  };

  const updateFilter = ({
    op,
    values,
  }: IUpdateFilter) => {
    const { id } = values;
    if (id) {
      setFilters((prev) => {
        const {
          key,
          operation,
          values: valuesFilter,
          description,
        } = values;

        switch (op) {
          case 'id':
            if (id && key) {
              prev[id] = { id: prev[id].id, key } as IFiltroOptions;
            }
            break;
          case 'op':
            if (id && operation) {
              prev[id].operation = operation;
            }
            break;
          case 'vl':
            if (id) {
              prev[id].values = valuesFilter;
              prev[id].description = description;
            }
            break;
        }

        return { ...prev };
      });
      setLastFilterUpdateId(id);
    }
  };

  const deleteFilter = (id: string) => {
    setFilters((prev) => {
      delete prev[id];
      return { ...prev };
    });
  };

  const onChange = (
    selecteds: any,
    selectedRows: INotification[],
  ) => {
    setCurrentSelectedRow(selectedRows);
  };

  const rowSelection = {
    onChange,
    columnWidth: 56,
  };

  const handleOrder = (key: INotificationOrderKey) => {
    if (!key) { return; }

    setOrder((prev) => {
      const newState = {
        notifications: null,
        users: null,
        data: null,
        obra: null,
        last: null,
      };
      if (prev[key] === null) {
        return {
          ...newState,
          [key]: 'ascend',
          last: key,
        };
      }
      if (prev[key] === 'ascend') {
        return {
          ...newState,
          [key]: 'descend',
          last: key,
        };
      }
      return {
        ...newState,
        [key]: null,
        last: null,
      };
    });
  };

  const sortedNotifications = useMemo(() => {
    let filteredNotifications = notifications;

    if (showOnlyUnread) {
      filteredNotifications = filteredNotifications.filter((x) => !x?.IsRead);
    }

    if (!!order.last) {
      if (order.last === 'notifications') {
        if (order.notifications === 'ascend') {
          return filteredNotifications.sort((a, b) => {
            return a.Message.localeCompare(b.Message);
          });
        }
        return filteredNotifications.sort((a, b) => {
          return b.Message.localeCompare(a.Message);
        });
      }
      if (order.last === 'data') {
        if (order.data === 'ascend') {
          return filteredNotifications.sort((a, b) => {
            return dayjs(b.CreationDate).isBefore(dayjs(a.CreationDate)) ? 1 : -1;
          });
        }
        return filteredNotifications.sort((a, b) => {
          return dayjs(a.CreationDate).isBefore(dayjs(b.CreationDate)) ? 1 : -1;
        });
      }
    }

    return filteredNotifications.sort((a, b) => {
      return dayjs(b.CreationDate).isAfter(dayjs(a.CreationDate)) ? 1 : -1;
    });
  }, [notifications, order, showOnlyUnread]);

  const setSelectedAsRead = async () => {
    const notificationAsView: Record<string, INotificationMultipleReadRequest> = {};
    for (const row of currentSelectedRows) {
      const readKeyId = `${row.ConstructionSiteFk}-${row.ItemAuxId}`;
      if (readKeyId in notificationAsView) {
        notificationAsView[readKeyId].notificationIds.push(row.NotificationId);
      } else {
        notificationAsView[readKeyId] = {
          constructionSiteId: row.ConstructionSiteFk,
          notificationIds: [row.NotificationId],
          itemAuxId: row.ItemAuxId,
        };
      }
    }
    const arrayForReadNotifications = Object.values(notificationAsView);
    setReadingNotificationIds((prev) => {
      return [
        ...prev,
        ...arrayForReadNotifications.map((x) => x.notificationIds).flat(),
      ];
    });
    if (arrayForReadNotifications.length > 0) {
      arrayForReadNotifications.forEach(async (x) => {
        await readMultipleNotificationsAsync(x);
      });
    }
    setReadingNotificationIds((prev) => {
      return prev.filter(
        (id) => !arrayForReadNotifications.some((x) => x.notificationIds.includes(id)),
      );
    });
  };

  const setAllAsRead = async () => {
    await readAllNotificationsAsync({
      filters: filtersQuery,
    });
  };

  const handleRead = async (item: INotification) => {
    setReadingNotificationIds((prev) => [...prev, item.NotificationId]);
    if (item?.AggNotificationIds && item.AggNotificationIds?.length > 0) {
      await readMultipleNotificationsAsync({
        constructionSiteId: item.ConstructionSiteFk,
        notificationIds: [item.NotificationId, ...item?.AggNotificationIds || []],
        itemAuxId: item.ItemAuxId,
      });
    } else {
      await readNotificationAsync({
        notificationId: item.NotificationId,
        constructionSiteId: item.ConstructionSiteFk,
        itemAuxId: item.ItemAuxId,
        filters: filtersQuery,
      });
    }
    setReadingNotificationIds((prev) => {
      return prev.filter((id) => id !== item.NotificationId);
    });
  };

  const handleRedirect = useCallback((item: INotification) => {
    // Handle BCF Export Completed - download file directly
    if (item.Action === NotificationActionEnum.BcfExportCompleted && item.ItemId) {
      const downloadFile = async () => {
        try {
          message.info('Preparando arquivo para download...');
          const response = await BcfImportApi.downloadExport(item.ItemId);
          const blob = new Blob([response.data], { type: 'application/zip' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `bcf-export-${new Date().toISOString().slice(0, 10)}.bcfzip`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          message.success('Download iniciado!');
        } catch (error) {
          console.error('Failed to download BCF export:', error);
          message.error('Erro ao fazer download do arquivo');
        }
      };
      downloadFile();
      return;
    }

    if (item?.ItemUrl) {
      if (item.Type !== NotificationTypeEnum.Activity) {
        const notificationAsView: INotificationMultipleReadRequest = {
          constructionSiteId: item.ConstructionSiteFk,
          notificationIds: [item.NotificationId, ...item?.AggNotificationIds || []],
        };
        addNotificationsToRead(
          item.ItemId,
          notificationAsView,
          item.Type === NotificationTypeEnum.Plotting ? undefined : item.ItemUrl,
        );
        return historyPush(item.ItemUrl);
      }
      if (item.Type === NotificationTypeEnum.Activity) {
        let activityId: string | null = null;
        if (filterItemIdByActivityAction.includes(item.Action)) {
          activityId = item?.ItemId;
        }
        if (filterItemAuxIdByActivityAction.includes(item.Action)) {
          activityId = item?.ItemAuxId;
        }
        if (!activityId) { return; }

        dispatch(ActivitiesActions.clearListActivities());
        const notificationsById = notifications.filter((notification) => {
          return notification?.ItemAuxId === activityId ||
            notification?.ItemId === activityId;
        });
        const notificationsReadData: Record<string, INotificationMultipleReadRequest> = {};
        for (const notification of notificationsById) {
          const readKeyId = `${notification.ConstructionSiteFk}-${notification.ItemAuxId}`;
          if (readKeyId in notificationsReadData) {
            notificationsReadData[readKeyId].notificationIds.push(notification.NotificationId);
          } else {
            notificationsReadData[readKeyId] = {
              constructionSiteId: notification.ConstructionSiteFk,
              notificationIds: [notification.NotificationId],
              itemAuxId: notification.ItemAuxId,
            };
          }

          if (
            notification?.AggNotificationIds &&
            notification.AggNotificationIds?.length > 0
          ) {
            for (const n of notification.AggNotificationIds) {
              const readKeyIdAgg = `${notification.ConstructionSiteFk}-${notification.ItemAuxId}`;
              if (readKeyIdAgg in notificationsReadData) {
                notificationsReadData[readKeyIdAgg].notificationIds.push(n);
              } else {
                notificationsReadData[readKeyIdAgg] = {
                  constructionSiteId: notification.ConstructionSiteFk,
                  notificationIds: [n],
                  itemAuxId: notification.ItemAuxId,
                };
              }
            }
          }
        }
        dispatch(ActivitiesActions.readNotificationsData({
          activityId,
          readData: notificationsReadData,
        }));
        return historyPush(item.ItemUrl);
      }
    }
  }, [historyPush, dispatch, notifications, addNotificationsToRead]);

  const moreActionsOptions: IOptionsMoreFiltersActions[] = [
    {
      label: t('page.viewOld'),
      icon: 'openNewTab',
      iconCustomSize: 14,
      onAction: onToggle,
    },
  ];

  const buttonsTableToolbar: ITableActionTopbarButton[] = [
    {
      description: t('page.showOnlyUnread'),
      iconComponent: (
        <SwitchStyled
          checked={showOnlyUnread}
          size='small'
          onChange={() => setShowOnlyUnread((prev) => !prev)}
        />
      ),
    },
    {
      icon: 'filtrar',
      iconCustomSize: 14,
      description: t('page.filters'),
      hidden: false,
      action: () => handleShowFilters(!showFilters),
    },
    {
      icon: 'confirmar',
      description: currentSelectedRows.length > 0
        ? t('page.markSelectedAsRead')
        : t('page.markAllAsRead'),
      iconCustomSize: 10,
      iconLoading: isLoadingReadAllNotifications,
      action: currentSelectedRows.length > 0 ? setSelectedAsRead : setAllAsRead,
    },
    {
      icon: 'moreActions',
      hidden: false,
      dropdownMenu: (toggleDropdown: () => void) => (
        <MoreFiltersActions
          options={moreActionsOptions}
          onClose={() => toggleDropdown()}
        />
      ),
    },
  ];

  useEffect(() => {
    if (!userInfo || !currentTenant) { return; }
    const notificationsStoragekey = `${NOTIFICATION_FILTERS_KEY}-${userInfo.Id}-${currentTenant.TenantId}`;

    if (firstRenderFilters.current) {
      const filtersStorage = AppStorage.GetItem(
        notificationsStoragekey,
        'local',
      );
      if (filtersStorage) {
        const parsedFilters = JSON.parse(filtersStorage) as FiltersRecordType;
        setFilters(parsedFilters);
        handleShowFilters(true);
      }
      firstRenderFilters.current = false;
    }
    if (!firstRenderFilters.current) {
      if (!isObjectEmpty(filters)) {
        AppStorage.SetItem(
          notificationsStoragekey,
          JSON.stringify(filters),
          'local',
        );
      } else {
        AppStorage.RemoveItem(notificationsStoragekey, 'local');
      }
    }
    if (
      !externalGlobalLoading &&
      !isLoadingListAll &&
      !firstRenderFilters.current &&
      firstOpenFilters.current
    ) {
      setTimeout(() => {
        firstOpenFilters.current = false;
      }, 1500);
    }
  }, [
    filters,
    externalGlobalLoading,
    isLoadingListAll,
    userInfo,
    currentTenant,
  ]);

  const readNotificationLoading = isLoadingReadNotification ||
    isLoadingReadMultipleNotifications;

  return (
    <>
      <NewNotificationGlobalStyles />
      <PageHeader name={t('page.title')}>
        <BreadCrumb breadcrumbs={breadCrumb} />
        <Space />
        <WrapperTable>
          {isLoadingListAll && <LoadingNewNotification multiple={13} />}
          {!isLoadingListAll &&
            <div className='tableContainer'>
              <TableActionTopbar buttons={buttonsTableToolbar} />
              {showFilters && (
                <Filters
                  filters={filters}
                  lastFilterUpdateId={lastFilterUpdateId}
                  firstOpenFilters={firstOpenFilters.current}
                  blockFilterDropdown={blockFilterDropdown.current}
                  addFilter={addFilter}
                  updateFilter={updateFilter}
                  deleteFilter={deleteFilter}
                />
              )}
              <Table
                virtual
                scroll={{
                  x: width - leftBarAndPaddingWidth,
                  y: height - (isFetchingNextPage ? 353 : 331),
                }}
                rowKey={(record: INotification) => record.NotificationId}
                columns={tableColumns({
                  obrasMap,
                  order,
                  theme,
                  readingNotificationIds,
                  readNotificationLoading,
                  onOrder: handleOrder,
                  onRead: handleRead,
                  onRedirect: handleRedirect,
                })}
                onScroll={(e) => {
                  const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
                  const isBottom = scrollTop + clientHeight >= scrollHeight - 20;
                  if (isBottom && hasNextPage && !isFetchingNextPage) {
                    fetchNextPage();
                  }
                }}
                dataSource={[...sortedNotifications]}
                className='listTable'
                rowClassName='fileListRow'
                rowSelection={rowSelection}
                footer={() => (
                  isFetchingNextPage && (
                    <div className='footerTable'>
                      <SpinCustom
                        size={22}
                        color={theme.colors.surface.onSurfaceVariant}
                      />
                    </div>
                  )
                )}
              />
            </div>
          }
        </WrapperTable>
      </PageHeader>
    </>
  );
};

export default NewNotification;
